import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FileText, Plus, Trash2, ChevronLeft, ChevronRight, Pencil, CalendarDays, Hash, Briefcase, Users } from "lucide-react";
import InlineEdit from "@/components/InlineEdit";
import ContratoFormDialog from "@/components/ContratoFormDialog";
import { useConfirm } from "@/components/ConfirmProvider";
import MandanteLogo from "@/components/MandanteLogo";

export default function ContratoDetail() {
  const confirmAction = useConfirm();
  const { id: mid, cid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [mandante, setMandante] = useState(null);
  const [contrato, setContrato] = useState(null);
  const [docs, setDocs] = useState([]);
  const [newDoc, setNewDoc] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = ["ADMIN", "HR_MANAGER"].includes(user?.role);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const s = String(iso).slice(0, 10);
    const [y, m, d] = s.split("-");
    return y && m && d ? `${d}-${m}-${y}` : s;
  };

  const load = async () => {
    try {
      const [m, c, d] = await Promise.all([
        api.get("/mandantes").then((r) => r.data.find((x) => x.id === mid)),
        api.get(`/mandantes/${mid}/contratos/${cid}`).then((r) => r.data),
        api.get(`/mandantes/${mid}/contratos/${cid}/documentos`).then((r) => r.data),
      ]);
      if (!m || !c) { toast.error("Contrato no encontrado"); nav("/mandantes"); return; }
      setMandante(m); setContrato(c); setDocs(d);
    } catch (e) { toast.error("No se pudo cargar"); }
  };
  useEffect(() => { load(); }, [mid, cid]);

  const updateContrato = async (payload) => {
    const { data } = await api.put(`/mandantes/${mid}/contratos/${cid}`, payload);
    setContrato((s) => ({ ...s, ...data }));
    toast.success("Contrato actualizado");
    load();
  };
  const addDoc = async () => {
    if (!newDoc.trim()) return;
    try {
      const { data } = await api.post(`/mandantes/${mid}/contratos/${cid}/documentos`, { name: newDoc });
      setDocs([...docs, data]); setNewDoc(""); toast.success("Documento agregado");
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo agregar"); }
  };
  const renameDoc = async (d, name) => {
    const { data } = await api.put(`/mandantes/${mid}/contratos/${cid}/documentos/${d.id}`, { name });
    setDocs(docs.map((x) => x.id === d.id ? { ...x, ...data } : x));
    toast.success("Documento renombrado");
  };
  const removeDoc = async (d) => {
    const ok = await confirmAction({ title: `Eliminar estándar "${d.name}"`, description: "El documento se eliminará del estándar del contrato.", confirmText: "Eliminar", variant: "danger" });
    if (!ok) return;
    await api.delete(`/mandantes/${mid}/contratos/${cid}/documentos/${d.id}`);
    setDocs(docs.filter((x) => x.id !== d.id)); toast.success("Documento eliminado");
  };

  if (!contrato || !mandante) return <div className="text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-6">
      <nav className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
        <Link to="/mandantes" className="hover:text-indigo-600 inline-flex items-center gap-1" data-testid="breadcrumb-mandantes"><ChevronLeft size={14} /> Mandantes</Link>
        <ChevronRight size={14} />
        <Link to={`/mandantes/${mid}`} className="hover:text-indigo-600" data-testid="breadcrumb-mandante">{mandante.name}</Link>
        <ChevronRight size={14} />
        <span className="text-slate-700 font-medium">{contrato.name}</span>
      </nav>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3 min-w-0">
            <MandanteLogo mandante={mandante} size="lg" />
            <div className="min-w-0">
              <h1 data-testid="contrato-glosa" className="font-display text-3xl font-bold text-slate-900">{contrato.name}</h1>
              <p className="text-slate-500 text-sm mt-1">Configuración del contrato · {mandante.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canEdit && (
              <button data-testid="edit-contrato-btn" onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-3 py-1.5 rounded-lg">
                <Pencil size={14} /> Editar
              </button>
            )}
          </div>
        </div>
        <table className="w-full text-sm">
          <tbody>
            <tr className="border-t border-slate-100"><Th icon={Hash} label="N° Contrato" /><Td mono>{contrato.numero_contrato || "—"}</Td></tr>
            <tr className="border-t border-slate-100"><Th icon={CalendarDays} label="Fecha de Inicio" /><Td>{formatDate(contrato.fecha_inicio)}</Td></tr>
            <tr className="border-t border-slate-100"><Th icon={CalendarDays} label="Fecha de Término" /><Td>{formatDate(contrato.fecha_termino)}</Td></tr>
            <tr className="border-t border-slate-100"><Th icon={Users} label="Dotación" /><Td>{contrato.dotacion ?? "—"}</Td></tr>
            <tr className="border-t border-slate-100"><Th icon={Briefcase} label="Gerencia" /><Td>{contrato.gerencia || "—"}</Td></tr>
          </tbody>
        </table>
      </div>

      <section className="bg-white rounded-2xl border border-slate-200 shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="font-display font-semibold text-slate-800 flex items-center gap-2"><FileText size={18} className="text-emerald-600" /> Estándar: Documentos del contrato</h2>
          <p className="text-xs text-slate-400 mt-0.5">Compliance propio del contrato (ej: Contrato / OC, Jornadas Excepcionales). Las cargas de archivos se hacen en Acreditación.</p>
        </div>
        <table data-testid="contrato-docs-table" className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              <th className="px-5 py-2 w-10">#</th>
              <th className="px-5 py-2">Documento</th>
              {canEdit && <th className="px-5 py-2 text-right w-20"></th>}
            </tr>
          </thead>
          <tbody>
            {docs.map((d, i) => (
              <tr key={d.id} data-testid={`contrato-doc-${d.id}`} className="border-t border-slate-50 hover:bg-slate-50">
                <td className="px-5 py-2.5 font-mono text-[11px] text-slate-400">{i + 1}</td>
                <td className="px-5 py-2.5">
                  <InlineEdit testid={`contrato-doc-name-${d.id}`} value={d.name} disabled={!canEdit}
                    onSave={(name) => renameDoc(d, name)} className="text-slate-700" />
                </td>
                {canEdit && (
                  <td className="px-5 py-2.5 text-right">
                    <button data-testid={`del-contrato-doc-${d.id}`} onClick={() => removeDoc(d)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
            {docs.length === 0 && <tr><td colSpan={3} className="px-5 py-4 text-sm text-slate-400 italic">Sin documentos definidos.</td></tr>}
          </tbody>
        </table>
        {canEdit && (
          <div className="flex gap-2 p-4 border-t border-slate-100">
            <input data-testid="new-contrato-doc-input" value={newDoc} onChange={(e) => setNewDoc(e.target.value)} placeholder="Nuevo documento del contrato…"
              onKeyDown={(e) => e.key === "Enter" && addDoc()}
              className="flex-1 px-3 py-1.5 rounded-md border border-slate-200 focus:border-emerald-400 outline-none text-sm" />
            <button data-testid="add-contrato-doc-button" onClick={addDoc} className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md"><Plus size={13} /> Documento</button>
          </div>
        )}
      </section>

      <ContratoFormDialog open={editOpen} onOpenChange={setEditOpen}
        initial={contrato} mandanteName={mandante.name}
        onSubmit={updateContrato} />
    </div>
  );
}

function Th({ icon: Icon, label }) {
  return (
    <th className="text-left px-5 py-2 text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50/60 w-56 whitespace-nowrap font-medium">
      <span className="inline-flex items-center gap-1"><Icon size={11} /> {label}</span>
    </th>
  );
}
function Td({ children, mono = false }) {
  return <td className={`px-5 py-2 text-sm text-slate-700 ${mono ? "font-mono" : ""}`}>{children}</td>;
}
